import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { githubService, GitHubStatus } from '../services/github.service';
import { authService } from '../services/auth.service';
import { Popover } from '../components/ui/Popover';
import { ApiError } from '../services/api';

export function SettingsPage() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [githubStatus, setGithubStatus] = useState<GitHubStatus | null>(null);
  const [githubLoading, setGithubLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load GitHub connection status.
  useEffect(() => {
    githubService
      .getStatus()
      .then(setGithubStatus)
      .catch(() => {
        // If GitHub integration is not configured (503), show disconnected silently.
        setGithubStatus({ connected: false });
      });
  }, []);

  // Handle ?github=connected / ?github=error from OAuth callback redirect.
  useEffect(() => {
    const github = searchParams.get('github');
    if (github === 'connected') {
      showToast('GitHub connected successfully.', 'success');
      setSearchParams({}, { replace: true });
      // Refresh status.
      githubService
        .getStatus()
        .then(setGithubStatus)
        .catch(() => {});
    } else if (github === 'error') {
      showToast('GitHub connection failed. Please try again.', 'error');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, showToast]);

  async function handleConnectGitHub() {
    setGithubLoading(true);
    try {
      const authURL = await githubService.getAuthURL();
      window.location.href = authURL;
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to start GitHub connection.';
      showToast(msg, 'error');
      setGithubLoading(false);
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    try {
      await authService.deleteAccount();
      await logout();
      navigate('/?deleted=true', { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to delete account.';
      showToast(msg, 'error');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  async function handleDisconnectGitHub() {
    setGithubLoading(true);
    try {
      await githubService.disconnect();
      setGithubStatus({ connected: false });
      showToast('GitHub disconnected.', 'success');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to disconnect GitHub.';
      showToast(msg, 'error');
    } finally {
      setGithubLoading(false);
    }
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Settings</h1>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Account Information</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-gray-900">{user?.email}</p>
          </div>
          {user?.name && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <p className="text-gray-900">{user.name}</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">GitHub</h2>
        <p className="text-sm text-gray-600 mb-4">
          Connect your GitHub account to push generated projects directly to repositories.
        </p>
        {githubStatus === null ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : githubStatus.connected ? (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm font-medium text-gray-900">
                Connected as{' '}
                <a
                  href={`https://github.com/${githubStatus.github_username}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  @{githubStatus.github_username}
                </a>
              </span>
            </div>
            <button
              onClick={handleDisconnectGitHub}
              disabled={githubLoading}
              className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              {githubLoading ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        ) : (
          <button
            onClick={handleConnectGitHub}
            disabled={githubLoading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path
                fillRule="evenodd"
                d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                clipRule="evenodd"
              />
            </svg>
            {githubLoading ? 'Redirecting…' : 'Connect GitHub →'}
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-4">Preferences</h2>
        <p className="text-gray-600 text-sm">Settings options will be added in future updates</p>
      </div>

      {/* Danger zone */}
      <div className="bg-white rounded-lg border border-red-200 p-6">
        <h2 className="text-xl font-semibold text-red-700 mb-2">Delete account</h2>
        <p className="text-sm text-gray-600 mb-4">
          Permanently delete your account and all projects. This cannot be undone.
        </p>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
        >
          Delete my account
        </button>
      </div>

      <Popover
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete your account?"
      >
        <p className="text-sm text-gray-600 mb-6">
          This will permanently delete all your projects, previews, and generated code. Your GitHub
          connection will be removed. This cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteAccount}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {deleting ? 'Deleting…' : 'Yes, delete everything'}
          </button>
        </div>
      </Popover>
    </div>
  );
}
